import { createBrowserRouter, Navigate } from "react-router-dom";
import App from "../App";
import { ProtectedRoute } from "../components/ProtectedRoute";
import { RootRedirect } from "../components/RootRedirect";
import { RouteErrorBoundary } from "../components/ErrorBoundary";
import LoginPage from "../pages/auth/LoginPage";
import RegisterPage from "../pages/auth/RegisterPage";
import VerifyEmailPage from "../pages/auth/VerifyEmailPage";
import ForgotPasswordPage from "../pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "../pages/auth/ResetPasswordPage";
import NotFoundPage from "../pages/NotFoundPage";
import ProjectDashboard from "../pages/projects/ProjectDashboard";
import ProjectDetail from "../pages/projects/ProjectDetail";
import OrganizationListPage from "../pages/organizations/OrganizationListPage";
import OrganizationDetailPage from "../pages/organizations/OrganizationDetailPage";
import CreateOrganizationPage from "../pages/organizations/CreateOrganizationPage";
import OrganizationSettingsPage from "../pages/organizations/OrganizationSettingsPage";
import InviteMemberPage from "../pages/organizations/InviteMemberPage";
import TeamListPage from "../pages/teams/TeamListPage";
import TeamDetailPage from "../pages/teams/TeamDetailPage";
import CreateTeamPage from "../pages/teams/CreateTeamPage";
import AddTeamMemberPage from "../pages/teams/AddTeamMemberPage";
import BillingPage from "../pages/organizations/BillingPage";
import VoiceProfilePage from "../pages/voiceProfile/VoiceProfilePage";
import SettingsPage from "../pages/settings/SettingsPage";

import {
  loginAction,
  registerAction,
  forgotPasswordAction,
  resetPasswordAction,
  resendVerificationAction,
  createOrganizationAction,
  updateOrganizationAction,
  deleteOrganizationAction,
  inviteMemberAction,
  updateMemberRoleAction,
  removeMemberAction,
  createTeamAction,
  updateTeamAction,
  deleteTeamAction,
  addTeamMemberAction,
  removeTeamMemberAction,
  createCheckoutAction,
  createPortalAction,
  cancelSubscriptionAction,
} from "./actions";

import {
  organizationDetailLoader,
  organizationSettingsLoader,
  inviteMemberLoader,
  teamListLoader,
  teamDetailLoader,
  addTeamMemberLoader,
  createTeamLoader,
  billingLoader,
  voiceProfileLoader,
} from "./loaders";

export const router = createBrowserRouter([
  {
    path: "/",
    element: <App />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <RootRedirect />,
      },
      {
        path: "login",
        element: <LoginPage />,
        action: loginAction,
      },
      {
        path: "register",
        element: <RegisterPage />,
        action: registerAction,
      },
      {
        path: "verify-email",
        element: <VerifyEmailPage />,
      },
      {
        path: "api/auth/resend-verification-action",
        action: resendVerificationAction,
      },
      {
        path: "forgot-password",
        element: <ForgotPasswordPage />,
        action: forgotPasswordAction,
      },
      {
        path: "reset-password/:token",
        element: <ResetPasswordPage />,
        action: resetPasswordAction,
      },
      {
        path: "dashboard",
        element: (
          <ProtectedRoute requireEmailVerified>
            <ProjectDashboard />
          </ProtectedRoute>
        ),
      },
      {
        path: "projects",
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: "organizations/:id/projects/:projectId",
        element: (
          <ProtectedRoute requireEmailVerified>
            <ProjectDetail />
          </ProtectedRoute>
        ),
      },
      {
        path: "organizations",
        element: (
          <ProtectedRoute requireEmailVerified>
            <OrganizationListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "organizations/create",
        element: (
          <ProtectedRoute requireEmailVerified>
            <CreateOrganizationPage />
          </ProtectedRoute>
        ),
        action: createOrganizationAction,
      },
      {
        path: "organizations/:id",
        element: (
          <ProtectedRoute requireEmailVerified>
            <OrganizationDetailPage />
          </ProtectedRoute>
        ),
        loader: organizationDetailLoader,
      },
      {
        path: "organizations/:id/settings",
        element: (
          <ProtectedRoute requireEmailVerified>
            <OrganizationSettingsPage />
          </ProtectedRoute>
        ),
        loader: organizationSettingsLoader,
        action: updateOrganizationAction,
      },
      {
        path: "organizations/:id/settings/delete",
        action: deleteOrganizationAction,
      },
      {
        path: "organizations/:id/billing",
        element: (
          <ProtectedRoute requireEmailVerified>
            <BillingPage />
          </ProtectedRoute>
        ),
        loader: billingLoader,
      },
      {
        path: "organizations/:id/billing/checkout",
        action: createCheckoutAction,
      },
      {
        path: "organizations/:id/billing/portal",
        action: createPortalAction,
      },
      {
        path: "organizations/:id/billing/cancel",
        action: cancelSubscriptionAction,
      },
      {
        path: "organizations/:id/members/invite",
        element: (
          <ProtectedRoute requireEmailVerified>
            <InviteMemberPage />
          </ProtectedRoute>
        ),
        loader: inviteMemberLoader,
        action: inviteMemberAction,
      },
      {
        path: "organizations/:id/members/:userId/role",
        action: updateMemberRoleAction,
      },
      {
        path: "organizations/:id/members/:userId/remove",
        action: removeMemberAction,
      },
      {
        path: "organizations/:id/teams",
        element: (
          <ProtectedRoute requireEmailVerified>
            <TeamListPage />
          </ProtectedRoute>
        ),
        loader: teamListLoader,
      },
      {
        path: "organizations/:id/teams/create",
        element: (
          <ProtectedRoute requireEmailVerified>
            <CreateTeamPage />
          </ProtectedRoute>
        ),
        loader: createTeamLoader,
        action: createTeamAction,
      },
      {
        path: "organizations/:id/teams/:teamId",
        element: (
          <ProtectedRoute requireEmailVerified>
            <TeamDetailPage />
          </ProtectedRoute>
        ),
        loader: teamDetailLoader,
        action: updateTeamAction,
      },
      {
        path: "organizations/:id/teams/:teamId/delete",
        action: deleteTeamAction,
      },
      {
        path: "organizations/:id/teams/:teamId/members/add",
        element: (
          <ProtectedRoute requireEmailVerified>
            <AddTeamMemberPage />
          </ProtectedRoute>
        ),
        loader: addTeamMemberLoader,
        action: addTeamMemberAction,
      },
      {
        path: "organizations/:id/teams/:teamId/members/:userId/remove",
        action: removeTeamMemberAction,
      },
      {
        path: "voice-profile",
        element: (
          <ProtectedRoute requireEmailVerified>
            <VoiceProfilePage />
          </ProtectedRoute>
        ),
        loader: voiceProfileLoader,
      },
      {
        path: "settings",
        element: (
          <ProtectedRoute requireEmailVerified>
            <SettingsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "*",
        element: <NotFoundPage />,
      },
    ],
  },
]);
